// src/lib/dashboard/negociacoesDaEtapa.ts
//
// Achata as negociações de uma etapa do funil para a lista que abre ao clicar
// no card do Comercial.
//
// A DATA de cada linha é a do evento daquela etapa, não a de criação da
// negociação: é assim que `computeEventFunnel` conta, e a lista tem que mostrar
// exatamente o que o card somou. Uma mesma negociação aparece em etapas
// diferentes com datas diferentes — e pode aparecer numa etapa e não na
// anterior, porque cada etapa olha o período pela data dela.
//
// Função pura, sem I/O nem JSX: roda sob `node --test` com type-stripping.

import type { Profile } from '../../../types.ts';
import type { EventFunnelRow, EventStage } from '../../../views/CRM/Analytics/eventFunnel.ts';
import { FUNNEL_STAGE_DEFS, defDaEtapa } from '../../../views/CRM/Analytics/eventFunnel.ts';
import { rotuloMotivoReprovacao } from '../termoReprovado.ts';
import { canalDaNegociacao } from './departamentoComercial.ts';
import type { DepartamentoComercial } from './departamentoComercial.ts';

export interface LinhaNegociacao {
  id: number;
  cliente: string;
  cpfCnpj: string | null;
  cidade: string | null;
  tipoCliente: string | null;
  origem: string | null;
  /** Nome de quem responde pela negociação. `null` = sem responsável no cadastro. */
  responsavel: string | null;
  canal: DepartamentoComercial;
  /** Data do evento DESTA etapa, em ISO. */
  data: string | null;
  kwh: number;
  /** Status atual da negociação — pode já ter avançado além da etapa clicada. */
  status: string;
  /**
   * Etapas ANTERIORES que a negociação nunca registrou (sem data nenhuma, em
   * período nenhum). Vazio = ela passou por todas.
   *
   * É o que explica um número que parece impossível: a Prefeitura de Morrinhos
   * foi criada em 02/09/2026 e aprovada em 03/09, sem uma simulação, sem termo
   * enviado e sem assinatura — aparece em "Termo aprovado" com 151.928 kWh sem
   * ter passado por nenhuma etapa do meio.
   */
  etapasPuladas: string[];
  /**
   * A etapa anterior que esta negociação registrou, com a data dela. `null`
   * quando não há nenhuma antes (ou todas foram puladas).
   */
  etapaAnterior: { label: string; data: string } | null;
  /**
   * `true` quando a etapa anterior aconteceu em OUTRO mês — o negócio começou
   * num mês e fechou em outro.
   *
   * Sem isso, o funil de um mês parece contraditório: o "CONDOMÍNIO DO EDIFÍCIO
   * CEDRO" teve o termo enviado em agosto e assinado em setembro, então entra em
   * "Termo Assinado" de setembro sem estar em "Termo Enviado" do mesmo mês.
   */
  atravessouMes: boolean;
  /** Só no card "Termo Reprovado" (#6331): por que reprovou. */
  motivoReprovacao?: string;
  /** A data da reprovação foi reconstruída (backfill), não é o momento exato. */
  dataAproximada?: boolean;
}

export const ROTULOS_CANAL: Record<DepartamentoComercial, string> = {
  todos: 'Todos',
  comercial: 'Comercial',
  parceiros: 'Parceiros',
  empresa_parceira: 'Empresa Parceira',
  licitacao: 'Licitação',
};

/**
 * kWh somado por canal, sobre as negociações recebidas.
 *
 * Não recorta nada sozinha: quem chama decide o universo. O seletor de canal
 * passa só as negociações com termo assinado no período — as mesmas linhas do
 * card "Termo Assinado". Usa o consumo da negociação, o mesmo número que os
 * cards do funil somam.
 */
export function volumeKwhPorCanal(
  negociacoes: readonly { origem?: string | null; responsavel_id?: string | null; consumo_medio_kwh?: number | string | null }[],
  profilesById: ReadonlyMap<string, Profile>,
): Map<DepartamentoComercial, number> {
  const mapa = new Map<DepartamentoComercial, number>();
  for (const n of negociacoes) {
    const canal = canalDaNegociacao(n, profilesById);
    mapa.set(canal, (mapa.get(canal) ?? 0) + (Number(n.consumo_medio_kwh) || 0));
  }
  return mapa;
}

/** Acima disto, o kWh da etapa é de uma negociação só e o card avisa. */
export const LIMITE_CONCENTRACAO = 0.5;

export interface ConcentracaoKwh {
  /** Fração do kWh da etapa que está na maior negociação. */
  fracao: number;
  /** Nome do cliente dessa negociação. */
  cliente: string;
  /** `true` quando passa de `LIMITE_CONCENTRACAO` e vale avisar no card. */
  concentrado: boolean;
}

/**
 * O quanto o kWh de uma etapa depende de uma única negociação.
 *
 * Existe porque o kWh do funil engana: em SET/2026, "Termo aprovado" somava
 * 152.383 kWh de 2 negócios, e 151.928 deles eram um cliente só. Sem esse
 * aviso, o card sugere um mês excepcional que não existiu.
 */
export function concentracaoKwh(linhas: readonly LinhaNegociacao[]): ConcentracaoKwh | null {
  const total = linhas.reduce((s, l) => s + l.kwh, 0);
  if (total <= 0 || linhas.length < 2) return null;
  const maior = linhas.reduce((a, b) => (b.kwh > a.kwh ? b : a));
  const fracao = maior.kwh / total;
  return { fracao, cliente: maior.cliente, concentrado: fracao >= LIMITE_CONCENTRACAO };
}

/**
 * As etapas que vêm antes de `key`, em ordem. Uma saída (Termo Reprovado) vem
 * logo depois da etapa de onde sai — não depois de "Termo Aprovado", senão toda
 * reprovada apareceria como tendo "pulado" a aprovação.
 */
export function etapasAntes(key: string) {
  const def = defDaEtapa(key);
  if (!def) return [];
  const ate = def.tipo === 'saida' && def.anterior
    ? FUNNEL_STAGE_DEFS.findIndex((d) => d.key === def.anterior) + 1
    : FUNNEL_STAGE_DEFS.findIndex((d) => d.key === key);
  return ate > 0 ? FUNNEL_STAGE_DEFS.slice(0, ate) : [];
}

/** Etapas anteriores a `key` que esta negociação nunca registrou. */
export function etapasPuladas(r: EventFunnelRow, key: string): string[] {
  return etapasAntes(key)
    .filter((d) => !d.dataDe(r))
    .map((d) => d.label);
}

/** A etapa registrada imediatamente antes de `key`, com a data dela. */
export function etapaAnteriorRegistrada(r: EventFunnelRow, key: string): { label: string; data: string } | null {
  const antes = etapasAntes(key);
  for (let i = antes.length - 1; i >= 0; i--) {
    const data = antes[i].dataDe(r);
    if (data) return { label: antes[i].label, data };
  }
  return null;
}

/** Duas datas ISO caíram em meses diferentes? */
export function mesesDiferentes(a: string | null, b: string | null): boolean {
  if (!a || !b) return false;
  return a.slice(0, 7) !== b.slice(0, 7);
}

/** "AGO/2026" a partir de uma data ISO. */
export function rotuloMes(iso: string | null): string {
  if (!iso) return '—';
  const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
  const ano = iso.slice(0, 4);
  const mes = Number(iso.slice(5, 7));
  return mes >= 1 && mes <= 12 ? `${MESES[mes - 1]}/${ano}` : '—';
}

export function linhasDaEtapa(
  stage: Pick<EventStage, 'key' | 'rows'>,
  profilesById: ReadonlyMap<string, Profile>,
): LinhaNegociacao[] {
  const def = defDaEtapa(stage.key);
  return stage.rows
    .map((r) => {
      const n = r as EventFunnelRow & Record<string, any>;
      const perfil = n.responsavel_id ? profilesById.get(String(n.responsavel_id)) : undefined;
      const data = def ? def.dataDe(r) : null;
      const anterior = etapaAnteriorRegistrada(r, stage.key);
      return {
        etapasPuladas: etapasPuladas(r, stage.key),
        etapaAnterior: anterior,
        atravessouMes: !!anterior && mesesDiferentes(anterior.data, data),
        id: Number(n.id),
        cliente: String(n.cliente_nome || '').trim() || 'Sem nome',
        cpfCnpj: n.cpf_cnpj ?? null,
        cidade: n.cidade ?? null,
        tipoCliente: n.tipo_cliente ?? null,
        origem: n.origem ?? null,
        responsavel: perfil?.full_name ?? null,
        canal: canalDaNegociacao(n, profilesById),
        data,
        kwh: Number(n.consumo_medio_kwh) || 0,
        status: String(n.status || ''),
        ...(stage.key === 'termo_reprovado'
          ? { motivoReprovacao: rotuloMotivoReprovacao(n.termo_reprovado_motivo), dataAproximada: !!n.termo_reprovado_aproximado }
          : {}),
      };
    })
    // Maior consumo primeiro: é o que explica o kWh do card, que costuma estar
    // concentrado em poucas negociações.
    .sort((a, b) => b.kwh - a.kwh || a.cliente.localeCompare(b.cliente, 'pt-BR'));
}
